import { auswahlAusEintrag, auswahlAusKnoten, kategorieFuerAbschnitt, knotenZuEintrag } from './auswahl';
import type { AufgeloesterKatalog, AufgeloesterKnoten, Gesamtkatalog, KatalogAuswahl, KatalogEintrag, MerkSchluessel } from './typen';

export { kategorieFuerAbschnitt } from './auswahl';

/**
 * Suche über die Torte (inkl. eigener Einträge) und den ganzen
 * Gesamtkatalog. Umlaute und ß sind egal („Fußleiste“ = „fussleiste“,
 * „Tür“ = „Tuer“); alle Suchwörter müssen treffen. Gesucht wird nur in
 * Texten (Kurztext, Name, Überschrift, Beschreibung, Pfad) — eine
 * Katalognummer gibt es nicht (Entscheid 28.09.).
 *
 * Die gefalteten Texte werden je Gesamtkatalog bzw. je Katalog einmal
 * berechnet (WeakMap) — eine Suche über ~1.900 Einträge bleibt damit bei
 * wenigen ms.
 */

/** klein, ß→ss, ä→ae, ö→oe, ü→ue, Diakritika weg, alles außer a–z/0–9 → ein Leerzeichen. */
export function falten(s: string): string {
    if (typeof s !== 'string' || !s) return '';
    return s
        .normalize('NFC')
        .toLowerCase()
        .replace(/ß/g, 'ss')
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .normalize('NFD')
        .replace(/\p{M}/gu, '')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}

export interface SuchTreffer {
    schluessel: MerkSchluessel;
    auswahl: KatalogAuswahl;
    /** Kleiner = besser. */
    rang: number;
}

// Rangstufen je Suchwort; der Rang eines Treffers ist die Summe über alle Wörter.
const R_KOPF = 2;
const R_TEXT = 4;
const R_KOPF_INNEN = 5;
const R_TEXT_INNEN = 6;
/** Ab dieser Länge trifft ein Suchwort auch mitten im Wort („leiter“ → „Anlegeleiter“). */
const MIN_INNEN = 4;

interface IndexEintrag {
    /** ' ' + gefaltete Kopftexte (Kurz, Name, Titel) — Wortanfang = ' ' + wort. */
    kopf: string;
    /** ' ' + gefalteter Text (Beschreibung, Pfad). */
    text: string;
}

interface KnotenIndex {
    knoten: AufgeloesterKnoten[];
    index: IndexEintrag[];
}

interface GesamtIndex {
    eintraege: KatalogEintrag[];
    index: IndexEintrag[];
}

const knotenIndizes = new WeakMap<AufgeloesterKatalog, KnotenIndex>();
const gesamtIndizes = new WeakMap<Gesamtkatalog, GesamtIndex>();

const mitRand = (teile: (string | undefined)[]): string => ' ' + falten(teile.filter(Boolean).join(' ')) + ' ';

/** Sichtbare Mängel in Baumreihenfolge (Tiefensuche entlang `kinder`). */
function maengelInReihenfolge(katalog: AufgeloesterKatalog): AufgeloesterKnoten[] {
    const aus: AufgeloesterKnoten[] = [];
    const besucht = new Set<string>();
    const gehen = (eltern: string) => {
        for (const id of katalog.kinder.get(eltern) ?? []) {
            if (besucht.has(id)) continue;
            besucht.add(id);
            const k = katalog.knoten.get(id);
            if (!k) continue;
            if (k.typ === 'mangel') {
                if (k.vorlage) aus.push(k);
            } else gehen(id);
        }
    };
    gehen('');
    return aus;
}

function knotenIndex(katalog: AufgeloesterKatalog): KnotenIndex {
    const vorhanden = knotenIndizes.get(katalog);
    if (vorhanden) return vorhanden;
    const knoten = maengelInReihenfolge(katalog);
    const index = knoten.map((k): IndexEintrag => ({
        kopf: mitRand([k.kurz.replace(/-\n/g, ''), k.name, k.vorlage?.titel]),
        text: mitRand([k.vorlage?.text, ...k.pfad.slice(0, -1)]),
    }));
    const ergebnis = { knoten, index };
    knotenIndizes.set(katalog, ergebnis);
    return ergebnis;
}

function gesamtIndex(quelle: Gesamtkatalog): GesamtIndex {
    const vorhanden = gesamtIndizes.get(quelle);
    if (vorhanden) return vorhanden;
    const eintraege = Array.isArray(quelle.eintraege) ? quelle.eintraege : [];
    const index = eintraege.map((e): IndexEintrag => ({
        kopf: mitRand([e.titel]),
        text: mitRand([e.text]),
    }));
    const ergebnis = { eintraege, index };
    gesamtIndizes.set(quelle, ergebnis);
    return ergebnis;
}

interface Anfrage {
    woerter: { wort: string; anfang: string }[];
}

/** Bester Rang eines Suchworts in einem Eintrag, sonst null. */
function wortRang(e: IndexEintrag, w: { wort: string; anfang: string }): number | null {
    if (e.kopf.includes(w.anfang)) return R_KOPF;
    if (e.text.includes(w.anfang)) return R_TEXT;
    if (w.wort.length >= MIN_INNEN) {
        if (e.kopf.includes(w.wort)) return R_KOPF_INNEN;
        if (e.text.includes(w.wort)) return R_TEXT_INNEN;
    }
    return null;
}

function eintragRang(e: IndexEintrag, a: Anfrage): number | null {
    let summe = 0;
    for (const w of a.woerter) {
        const r = wortRang(e, w);
        if (r === null) return null;
        summe += r;
    }
    return summe;
}

interface Kandidat {
    rang: number;
    /** 0 = Knoten, 1 = Eintrag des Gesamtkatalogs (Knoten davor bei gleichem Rang). */
    art: number;
    pos: number;
    bauen: () => SuchTreffer;
}

/**
 * Sucht in Torte/eigenen Einträgen und (falls geladen) im Gesamtkatalog.
 * Rang: Kurz/Titel-Wortanfang > Text > mitten im Wort; bei gleichem Rang
 * Knoten vor Einträgen des Gesamtkatalogs, dann Katalogreihenfolge. Ein
 * Eintrag, der als Knoten in der Torte steht (Standard über quelle[0] oder
 * als eigener Mangel übernommen), erscheint nur als Knoten.
 */
export function suchen(
    eingabe: string,
    katalog: AufgeloesterKatalog,
    quelle: Gesamtkatalog | null,
    zuordnung: Record<string, string>,
    max = 60,
): SuchTreffer[] {
    const gefaltet = falten(eingabe ?? '');
    if (!gefaltet || !(max > 0)) return [];
    const anfrage: Anfrage = {
        woerter: [...new Set(gefaltet.split(' '))].map((wort) => ({ wort, anfang: ' ' + wort })),
    };

    const kandidaten: Kandidat[] = [];
    const ki = knotenIndex(katalog);
    ki.index.forEach((e, pos) => {
        const rang = eintragRang(e, anfrage);
        if (rang === null) return;
        const k = ki.knoten[pos];
        kandidaten.push({ rang, art: 0, pos, bauen: () => ({ schluessel: 'knoten:' + k.id, auswahl: auswahlAusKnoten(k), rang }) });
    });

    if (quelle) {
        const gi = gesamtIndex(quelle);
        gi.index.forEach((e, pos) => {
            const eintrag = gi.eintraege[pos];
            const rang = eintragRang(e, anfrage);
            if (rang === null) return;
            if (knotenZuEintrag(katalog, eintrag)) return;
            kandidaten.push({
                rang,
                art: 1,
                pos,
                bauen: () => ({
                    schluessel: 'eintrag:' + eintrag.id,
                    auswahl: auswahlAusEintrag(eintrag, kategorieFuerAbschnitt(eintrag.abschnitt, quelle, zuordnung), katalog),
                    rang,
                }),
            });
        });
    }

    kandidaten.sort((a, b) => a.rang - b.rang || a.art - b.art || a.pos - b.pos);
    return kandidaten.slice(0, Math.floor(max)).map((k) => k.bauen());
}
